import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    selectEmissionsDrawerCardData,
    selectGlobalDate,
    selectMethaneProduct,
    selectPlatform,
    useGetMultipleRasterPointsByStacSearchUrlsQuery,
    useGetMultipleStacSearchesByUrlQuery,
    selectFlooredZoom
} from '../../reducers';
import {
    CaptureFeatureProperties,
    DateRanges,
    MethaneLayerIds,
    PickInfoToPrioritize,
    PlumeEmissionPickInfo,
    PlumeProperties,
    RasterPointFeature,
    STACCollection,
    StacFeature,
    StacFeatureWithLayerId,
    StacFilterLang,
    Platforms,
    UrlFeatureTypes
} from '../../types';
import {
    getCollectionNamesForLayer,
    getDateRangeFromPlatform,
    getDatetimeOneMonthRangeString,
    getDatetimeQuarterlyRangeString,
    getDatetimeYearlyRangeString,
    sortPickInfos
} from '../../utils';
import { getPointSourceItem } from './stacData';
import { replaceUrlSource } from '../pages/emissions-map/methaneLayers';
import { getArrayURLParams } from './emissionsDrawerSelection';
import { flatten } from 'lodash';
import { GeoJsonProperties, Geometry, Point, Polygon } from 'geojson';

/**
 * Builds the datetime filter for a target search.
 * Uses the target-specific day when present; otherwise falls back to the current platform bucket.
 */
const getTargetDateFilter = (targetDate: string | undefined, globalDate: number, platform: Platforms) => {
    if (typeof targetDate === 'string' && targetDate.length) {
        return `datetime=${targetDate}T00:00:00.000Z/${targetDate}T23:59:59.999Z`;
    }

    // If a sibling target (a target in another card ) has not been assigned a date,  search the
    // current platform bucket so we can resolve the latest capture within that range.
    const date = new Date(globalDate);
    const dateRange = getDateRangeFromPlatform(platform);

    if (dateRange === DateRanges.Quarter) {
        return `datetime=${getDatetimeQuarterlyRangeString(date)}`;
    }
    if (dateRange === DateRanges.Year) {
        return `datetime=${getDatetimeYearlyRangeString(date)}`;
    }
    return `datetime=${getDatetimeOneMonthRangeString(date)}`;
};

const getTargetSearchParams = (targetId: string, collectionIds: STACCollection[], dateFilter: string) => {
    const filterLang = StacFilterLang.cql2Text;
    //filter param example: target_id='53'
    const targetFilter = `target_id='${targetId}'`;
    // params for target data search
    return `collections=${collectionIds.join(',')}&filter-lang=${filterLang}&filter=${encodeURIComponent(targetFilter)}&${dateFilter}`;
};

/**
 *
 * Fetches the data of the methane feature that has been selected
 * (either through user interaction or URL params), and returns
 * it as a `PickInfoToPrioritize` object.
 *
 * @returns The layer information, or null if there is no selection or an error.
 */
export const useSelectedFeaturePickInfos = (
    shouldFetchOverride: boolean = true,
    allowEmptyResults: boolean = false
) => {
    const drawerData = useSelector(selectEmissionsDrawerCardData);

    const targetList = getArrayURLParams(drawerData?.targets);
    const selectionType = drawerData?.['selection-type'];

    const shouldFetch = shouldFetchOverride && !!selectionType && targetList.length > 0;

    const [selectedPointSources, setSelectedPointSources] = useState<AugmentedStacPointFeature[] | null>(null);

    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);
    const globalDate = useSelector(selectGlobalDate);
    const collectionIds = getCollectionNamesForLayer(product, platform, MethaneLayerIds.targets);

    const targetSearchStrings = targetList.map((targetId) => {
        const targetDate = drawerData[`target-${targetId}-date`];
        const dateFilter = getTargetDateFilter(
            typeof targetDate === 'string' ? targetDate : undefined,
            globalDate,
            platform
        );
        return getTargetSearchParams(targetId, collectionIds, dateFilter);
    });

    const flooredZoom = useSelector(selectFlooredZoom);
    // *********
    // request data for all selected targets
    // *********
    const { data: targetData, isFetching: isFetchingTargetData } = useGetMultipleStacSearchesByUrlQuery(
        {
            searchParamsList: targetSearchStrings,
            /**
             * do not filter because we may be requesting L3 data - in that case we have no need
             * for L4 data and it is safe to assume users have not clicked data without totals,
             * since those are filtered out of results in all other places
             */
            filterToResultsWithMatchingTotals: false
        },
        {
            skip: !shouldFetch
        }
    );
    const selectedTargetCaptures = useMemo(() => {
        if (!targetData || isFetchingTargetData) {
            return [];
        }

        return targetList.flatMap((targetId, idx) => {
            const selectedTargetItemId = drawerData?.[`target-${targetId}-itemId`];
            const selectedItemId = typeof selectedTargetItemId === 'string' ? selectedTargetItemId : null;
            const targetFeatures = targetData[idx]?.features;
            if (!targetFeatures?.length) return [];

            const item = selectedItemId
                ? targetFeatures.find((feature) => feature.id === selectedItemId) || targetFeatures[0]
                : targetFeatures[0];

            return item ? [{ targetId, item }] : [];
        });
    }, [drawerData, isFetchingTargetData, targetData, targetList]);

    // *********
    // collect data for targets with totals & no COGs
    // *********
    const totalsOnlyData = useMemo(
        () => selectedTargetCaptures.filter(({ item }) => !item.assets?.COG),
        [selectedTargetCaptures]
    );

    // *********
    // request data for all selected rasters
    // *********
    const coordinates = useMemo(() => [drawerData?.['selected-lng'], drawerData?.['selected-lat']], [drawerData]);
    const rasterUrls = useMemo(() => {
        const [lng, lat] = coordinates;
        return selectedTargetCaptures
            .filter(({ item }) => !!item.assets?.COG)
            .map(({ item: targetItem }) => {
                return {
                    url: `raster/collections/${targetItem.collection}/items/${targetItem.id}/point/${lng},${lat}?assets=COG`,
                    itemData: targetItem,
                    coordinates: [lng, lat]
                };
            });
    }, [coordinates, selectedTargetCaptures]);

    const { data: rasterResponses, isFetching: isFetchingRasterData } = useGetMultipleRasterPointsByStacSearchUrlsQuery(
        rasterUrls,
        { skip: !shouldFetch || selectionType !== 'details' || isFetchingTargetData }
    );

    const rasterData = rasterResponses?.map((rasterResponse) => rasterResponse.data);

    // *********
    // points
    // *********
    // mostly handled in the useEffect hook below
    const targetsWithPoints = Object.entries(drawerData).filter(([key]) => key.includes('-points'));

    const pointCollectionNames = getCollectionNamesForLayer(product, platform, MethaneLayerIds.plumeEmissionRate);
    const showPoints = !!targetsWithPoints.length;
    const pointSearchParamsList = targetsWithPoints.map(([key]) => {
        // Extracts the target id from the url key
        // examples:
        // * target-mair-uinta-points => mair-uinta
        // * target-57-points => 57
        // (replaces the whole key with only the text of the second capture group; capture groups are 1-indexed)
        const targetId = key.replace(/(^target-)(.*)(-points$)/, '$2');
        const targetDate = drawerData[`target-${targetId}-date`];
        const dateFilter = getTargetDateFilter(
            typeof targetDate === 'string' ? targetDate : undefined,
            globalDate,
            platform
        );
        return getTargetSearchParams(targetId, pointCollectionNames, dateFilter);
    });
    // gets the point source STAC items for all point sources in URL
    const { data: pointStacSearches, isFetching: isFetchingPointStacSearches } = useGetMultipleStacSearchesByUrlQuery(
        { searchParamsList: pointSearchParamsList, filterToResultsWithMatchingTotals: false },
        { skip: !shouldFetch || !showPoints }
    );
    useEffect(() => {
        if (showPoints && !isFetchingTargetData && !isFetchingPointStacSearches && pointStacSearches && targetData) {
            // Promise for the point data for each collect with points selected -
            Promise.all(
                // first, gets the point source geojson
                pointStacSearches.map((pointStacSearch) => {
                    const pointStacItem = pointStacSearch.features?.[0] as StacFeature<
                        Polygon,
                        CaptureFeatureProperties
                    >;
                    const pointSourceUrl =
                        pointStacItem?.assets?.GeoJSON?.href ?? pointStacItem?.assets?.growing_box_pruned?.href;
                    const updatedPointSourceUrl = pointSourceUrl && replaceUrlSource(pointSourceUrl);
                    const abortController = new AbortController();

                    // this gets the point source geojson; the function name is a little misleading
                    return getPointSourceItem({
                        pointItemId: pointStacItem.id,
                        stacCollectionId: pointStacItem.collection,
                        targetId: pointStacItem.properties.target_id,
                        pointSourceUrl: updatedPointSourceUrl,
                        abortController
                    });
                })
            ).then((pointSourceItems) => {
                const allPoints = pointSourceItems
                    .filter((i) => !!i)
                    .map((item, idx) => {
                        if (!item) return null;

                        // then filters each collect's point sources to the ones selected
                        const targetId = item.parentTargetId;
                        const itemId = item.id;

                        // use the related STAC search response for additional metadata
                        const collectionId = pointStacSearches[idx].features![0].properties.collection_id;

                        // Here we attach the stac item id for the related target/raster item.
                        // This is how we let the map know that the capture should get a heavy outline
                        // when the plume is interacted with.
                        //
                        // For MAIR showing point sources in targets without totals, we have to skip this
                        const relatedRasterItem = selectedTargetCaptures.find(
                            (targetItem) => targetItem.targetId === targetId
                        );
                        const relatedRasterItemId = relatedRasterItem?.item.id || '';

                        // List of point ids that should be selected, in each target
                        const pointList = drawerData[`target-${targetId}-points`] as string[];
                        const points = (item.features as StacFeature<Point>[]).filter((feature) => {
                            return pointList?.includes(feature.properties!.plume_id.toString());
                        });
                        const augmentedPoints: AugmentedStacPointFeature[] = points?.map((p) => {
                            return {
                                ...p,
                                properties: {
                                    ...p.properties,
                                    target_id: targetId,
                                    parentItemId: itemId,
                                    relatedRasterItemId,
                                    collection_id: collectionId
                                }
                            };
                        });

                        if (augmentedPoints) return augmentedPoints;
                        return [] as AugmentedStacPointFeature[];
                    })
                    .filter((point) => !!point);
                const flattenedPoints = flatten(allPoints);
                setSelectedPointSources(flattenedPoints as AugmentedStacPointFeature[]);
            });
        }
    }, [
        isFetchingTargetData,
        targetData,
        drawerData,
        pointStacSearches,
        isFetchingPointStacSearches,
        selectedTargetCaptures,
        showPoints
    ]);

    return useMemo(() => {
        if (
            !targetData ||
            isFetchingTargetData ||
            isFetchingRasterData ||
            isFetchingPointStacSearches ||
            (selectionType === 'details' && rasterUrls.length > 0 && !rasterResponses) ||
            (showPoints && !selectedPointSources)
        )
            return null;

        // collect a list of each target, raster and/or point data that should
        // be represented in the drawer
        const dataList = [];

        if (selectionType === 'target') {
            for (let i = 0, l = selectedTargetCaptures.length; i < l; i++) {
                const targetItem = selectedTargetCaptures[i];
                if (targetItem?.item) {
                    dataList.push({
                        type: UrlFeatureTypes.Target,
                        properties: {
                            itemId: targetItem.item.id,
                            targetId: targetItem.item.properties.target_id,
                            coordinates
                        },
                        data: {
                            stacItemData: targetItem.item
                        }
                    });
                }
            }
        } else if (selectionType === 'details') {
            if (rasterData) {
                rasterData
                    .filter((r) => !!r)
                    .forEach((raster) => {
                        dataList.push({
                            type: UrlFeatureTypes.AreaEmission,
                            properties: {
                                itemId: raster.id,
                                targetId: raster.properties.target_id,
                                coordinates
                            },
                            data: {
                                rasterData: raster
                            }
                        });
                    });
            }
            if (totalsOnlyData && totalsOnlyData.length) {
                totalsOnlyData.forEach((data) => {
                    dataList.push({
                        type: UrlFeatureTypes.TotalOnly,
                        properties: {
                            itemId: data.item.id,
                            targetId: data.item.properties.target_id,
                            coordinates
                        },
                        data: {
                            stacItemData: data.item
                        }
                    });
                });
            }
            if (showPoints && selectedPointSources) {
                selectedPointSources
                    .filter((p) => !!p)
                    .forEach((plume) => {
                        dataList.push({
                            type: UrlFeatureTypes.Plume,
                            properties: {
                                itemId: plume.properties.parentItemId,
                                targetId: plume.properties.target_id,
                                coordinates: plume.geometry.coordinates,
                                relatedRasterItemId: plume.properties.relatedRasterItemId
                            },
                            data: {
                                selectedPlume: {
                                    ...plume,
                                    // for now, we have only one point-source STAC collection per platform
                                    // if that changes, this may need to change as well.
                                    collection: pointCollectionNames[0],
                                    layerId: `${MethaneLayerIds.plumeEmissionRate}_${plume.properties?.parentItemId}`
                                }
                            }
                        });
                    });
            }
        }

        // transform that list of data into pickInfos
        const pickInfos = dataList
            .map(({ type, properties, data }) => formatDataAsPickInfo(type, properties, data))
            .filter((i) => !!i);
        if (pickInfos.length) return sortPickInfos(pickInfos as PickInfoToPrioritize[], flooredZoom);
        return allowEmptyResults ? [] : null;
    }, [
        allowEmptyResults,
        coordinates,
        flooredZoom,
        isFetchingPointStacSearches,
        isFetchingRasterData,
        isFetchingTargetData,
        pointCollectionNames,
        rasterResponses,
        rasterUrls.length,
        rasterData,
        selectedPointSources,
        selectionType,
        showPoints,
        targetData,
        selectedTargetCaptures,
        totalsOnlyData
    ]);
};

// todo: this is weird and maybe not necessary
// see https://methanesat.atlassian.net/browse/DP-5699
type AugmentedStacFeature<G extends Geometry = Geometry, P = GeoJsonProperties> = StacFeature<
    G,
    P & { target_id: string; parentItemId: string; collection_id: string }
>;
type AugmentedStacPointFeature = AugmentedStacFeature<Point, GeoJsonProperties & { relatedRasterItemId: string }>;

/**
 * Formats either the data fetched from STAC or the selected plume as a `PickInfoToPrioritize` object
 * @param featureType - The type of feature being processed (Target, AreaEmission, or Plume)
 * @param properties - Object containing itemId, targetId, and coordinates of the feature
 * @param data - Object containing the feature data (STAC item, raster data, and/or plume feature)
 * @returns A formatted `PickInfoToPrioritize` object or null
 */
export const formatDataAsPickInfo = (
    featureType: UrlFeatureTypes | null,
    properties: { itemId: string; targetId: string; coordinates: number[]; relatedRasterItemId?: string },
    data: {
        stacItemData?: StacFeature;
        rasterData?: RasterPointFeature;
        selectedPlume?: StacFeatureWithLayerId<GeoJSON.Point, PlumeProperties>;
    }
): Partial<PickInfoToPrioritize | PlumeEmissionPickInfo> | null => {
    const { itemId, targetId, coordinates } = properties;
    const { stacItemData, rasterData, selectedPlume } = data;

    switch (featureType) {
        case UrlFeatureTypes.Target:
            if (!stacItemData) return null;
            return {
                layer: { id: MethaneLayerIds.targets } as PickInfoToPrioritize['layer'],
                object: stacItemData
            };

        case UrlFeatureTypes.AreaEmission: {
            if (!rasterData || !Number.isFinite(rasterData.properties?.methane)) return null;
            return {
                layer: {
                    id: `${MethaneLayerIds.areaEmissionRaster}_${itemId}`,
                    itemId,
                    parentTargetId: targetId
                } as PickInfoToPrioritize['layer'],
                object: rasterData,
                coordinate: coordinates
            };
        }
        case UrlFeatureTypes.TotalOnly: {
            return {
                coordinate: coordinates,
                layer: {
                    id: `${MethaneLayerIds.areaEmissionRaster}-total-only-fill`,
                    itemId,
                    parentTargetId: targetId
                } as PickInfoToPrioritize['layer'],
                object: stacItemData
            };
        }
        case UrlFeatureTypes.Plume:
            if (!selectedPlume) return null;
            return {
                coordinate: selectedPlume.geometry.coordinates,
                layer: {
                    id: selectedPlume.layerId,
                    uniqueIdProperty: 'plume_id'
                } as PickInfoToPrioritize['layer'],
                object: {
                    ...selectedPlume,
                    itemId,
                    parentTargetId: targetId,
                    relatedRasterItemId: properties.relatedRasterItemId || ''
                },
                ...(selectedPlume.index ? { index: selectedPlume.index } : {})
            };
    }
    return null;
};
